import { Anchor, Box, Icon, Text } from '@zillow/constellation-10';
import { IconErrorFilled } from '@zillow/constellation-icons';
import Bullet from '../../Bullet';

// Define message status constants (single source of truth)
export const MESSAGE_STATUS = {
  SENT: 'sent',
  SENDING: 'sending',
  FAILED: 'failed',
} as const;

const MESSAGE_STATUS_VALUES = Object.values(MESSAGE_STATUS);

// Derive the type from the values above: 'sent' | 'sending' | 'failed'
export type MessageStatusVariant = (typeof MESSAGE_STATUS)[keyof typeof MESSAGE_STATUS];

export const isMessageStatusVariant = (value: unknown): value is MessageStatusVariant => {
  return typeof value === 'string' && MESSAGE_STATUS_VALUES.includes(value as MessageStatusVariant);
};

const MESSAGE_STATUS_TEXT: Record<MessageStatusVariant, string> = {
  sent: 'Sent',
  sending: 'Sending',
  failed: 'Failed',
} as const;

type MessageStatusIndicatorProps = {
  failureText?: string;
  resendMessage?: () => void;
  status: MessageStatusVariant;
  shouldDisplaySentStatus?: boolean;
};

export default function MessageStatusIndicator({
  failureText = MESSAGE_STATUS_TEXT.failed,
  resendMessage,
  status,
  shouldDisplaySentStatus,
}: MessageStatusIndicatorProps): JSX.Element | null {
  const isFailedStatus = status === MESSAGE_STATUS.FAILED;
  const statusText = MESSAGE_STATUS_TEXT[status];

  if (!statusText || (status === MESSAGE_STATUS.SENT && !shouldDisplaySentStatus)) return null;

  if (isFailedStatus) {
    return (
      <Box css={{ display: 'flex', alignItems: 'center' }}>
        <Icon
          css={{
            color: 'text.action.critical.hero.default',
            marginRight: 'tightest',
          }}
        >
          <IconErrorFilled />
        </Icon>

        <Text
          textStyle="body-xs"
          css={{
            color: 'text.action.critical.hero.default',
          }}
        >
          {failureText}
        </Text>

        {resendMessage && (
          <Anchor asChild css={{ textStyle: 'body-xs', display: 'inline', paddingLeft: 'tighter' }}>
            <button type="button" onClick={resendMessage}>
              Try again
            </button>
          </Anchor>
        )}

        <Bullet />
      </Box>
    );
  }

  return (
    <Text textStyle="body-xs" css={{ color: 'text.subtle' }}>
      {statusText}
      <Bullet />
    </Text>
  );
}
